
import { StyleInitializer } from './StyleInitializer';
import { StyleEventEmitter } from './StyleEventEmitter';
import type { StyleManagerConfig, StyleEventHandlers } from './types/StyleManagerTypes';

export class StyleManager {
  private shadow: ShadowRoot;
  private config: StyleManagerConfig;
  private initializer: StyleInitializer;
  private eventEmitter: StyleEventEmitter;
  private stylesLoaded: boolean = false;
  private styleLoadingTimeout: number | null = null;
  
  constructor(shadow: ShadowRoot, baseUrl: string) {
    this.shadow = shadow;
    this.config = { baseUrl };
    console.log('[DEBUG] StyleManager initialized with baseUrl:', baseUrl);
    console.log('[DEBUG] ShadowRoot mode:', shadow.mode);
    
    this.initializer = new StyleInitializer(shadow, this.config);
    this.eventEmitter = new StyleEventEmitter(shadow, {
      onStylesReady: () => this.handleStylesReady(),
      onStylesError: (error) => this.handleStylesError(error)
    });
  }
  
  addAllStyles(): void {
    console.log('[DEBUG] StyleManager: Adding all styles');
    
    // Add base styles immediately
    this.addBaseStyles();
    
    // After base styles are added, set a timer that will ensure we proceed even if external styles fail
    this.setStyleLoadingTimeout();
    
    // Mark styles as loaded and notify others
    this.stylesLoaded = true;
    this.eventEmitter.notifyStylesReady();
  }
  
  private addBaseStyles(): void {
    try {
      // Initialize all essential styles
      this.initializer.initializeStyles();
      console.log('[DEBUG] All base styles added successfully');
      
      // Add critical component styles to ensure fallback even without Tailwind
      this.addCriticalComponentStyles();
    } catch (error) {
      console.error('[ERROR] Error adding base styles:', error);
      this.addFallbackStyles();
    }
  }
  
  private addFallbackStyles(): void {
    console.log('[DEBUG] StyleManager: Adding fallback styles');
    try {
      const fallbackStyles = this.initializer.createFallbackStyles();
      this.shadow.appendChild(fallbackStyles);
      console.log('[DEBUG] Fallback styles added successfully');
    } catch (error) {
      console.error('[ERROR] Error adding fallback styles:', error);
    }
  }
  
  private addCriticalComponentStyles(): void {
    try {
      console.log('[DEBUG] StyleManager: Adding critical component styles');
      
      // Add specific styles for essential components that might not be covered by Tailwind
      const style = document.createElement('style');
      style.textContent = `
        /* Critical styles for calculator components */
        .onexip-logo-container {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
          background: white;
        }
        
        .onexip-logo {
          height: 2.5rem;
          display: block;
        }
        
        .onexip-calculator-container {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #0f172a;
          line-height: 1.5;
          width: 100%;
          max-width: 100%;
          background-color: white;
          border-radius: 0.5rem;
          overflow: hidden;
        }
        
        .onexip-calculator-wrapper {
          width: 100%;
          overflow: hidden;
          padding: 1rem;
        }
        
        /* Basic responsive layout */
        @media (max-width: 640px) {
          .onexip-calculator-wrapper {
            padding: 0.5rem;
          }
        }
      `;
      
      this.shadow.appendChild(style);
      console.log('[DEBUG] Critical component styles added successfully');
    } catch (error) {
      console.error('[ERROR] Error adding critical component styles:', error);
    }
  }
  
  private setStyleLoadingTimeout(): void {
    // Set a timeout to ensure we move forward even if styles don't load properly
    const timeout = 2000; // 2 seconds
    console.log(`[DEBUG] StyleManager: Setting style loading timeout of ${timeout}ms`);
    
    this.styleLoadingTimeout = window.setTimeout(() => {
      console.log('[WARN] Style loading timeout reached, continuing with fallback styles');
      this.clearStyleLoadingTimeout();
      
      // Ensure we have at least fallback styles
      this.addFallbackStyles();
      
      // Force styles ready notification
      this.eventEmitter.notifyStylesReady();
    }, timeout);
  }
  
  private clearStyleLoadingTimeout(): void {
    if (this.styleLoadingTimeout !== null) {
      window.clearTimeout(this.styleLoadingTimeout);
      this.styleLoadingTimeout = null;
      console.log('[DEBUG] Cleared styles timeout');
    }
  }
  
  private handleStylesReady(): void {
    this.stylesLoaded = true;
    this.clearStyleLoadingTimeout();
  }
  
  private handleStylesError(error: Error): void {
    console.error('[ERROR] Style loading error:', error);
    this.stylesLoaded = true; // Mark as loaded anyway to allow fallback styles
    this.clearStyleLoadingTimeout();
    console.log('[WARN] Falling back to base styles only');
    this.addFallbackStyles();
  }
  
  areStylesLoaded(): boolean {
    return this.stylesLoaded;
  }
  
  cleanup(): void {
    console.log('[DEBUG] StyleManager: Cleanup initiated');
    this.clearStyleLoadingTimeout();
    this.stylesLoaded = false;
  }
  
  notifyStylesReady(): void {
    console.log('[DEBUG] StyleManager: Notifying styles ready');
    this.eventEmitter.notifyStylesReady();
  }
}
